import { ReactElement, ReactNode } from "react";
import { useAtom } from "jotai";
import {
  Box,
  Center,
  Flex,
  Grid,
  IconButton,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import dayjs from "dayjs";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";
import { useMonthlyReport } from "../../../hooks/useMonthlyReport";
import { MonthlyReportContent } from "../../../stores/monthlyReportStore";
import {
  MimosysDataAscAtom,
  MimosysDateType,
  mimosysDataQueryAtom,
} from "../../../stores/mimosysStore";
import { outputStenScoreAtom } from "../../../store";
import { NumericDataText } from "../NumericDataText";

/*
 * ログイン後 解析結果画面 月次レポートタブの中身
 */
export function MonthlyReportBody(): ReactElement {
  const t = useModifiedTranslation();
  const {
    isAvailableLoading,
    isAvailableError,
    isEmpty,
    yearMonth,
    isNoDataMonth,
    report,
    isReportLoading,
    canDecrement,
    canIncrement,
    decrement,
    increment,
  } = useMonthlyReport();

  // 利用可能月に含まれない月は、測定データが無い場合の文言で各セクションを埋める
  const insufficient = {
    text: t("label_monthly_report_comment_insufficient"),
  };
  const content: MonthlyReportContent | null = isNoDataMonth
    ? {
        monthly_summary: {
          text: t("label_monthly_report_comment_monthly_summary_no_data"),
        },
        mind_index: insufficient,
        mental_activity: insufficient,
        emotion_balance: insufficient,
        measurement_status: {
          text: t("label_monthly_report_comment_measurement_status_no_data"),
        },
      }
    : report;

  if (isAvailableLoading) {
    return (
      <Center w="full" py={10}>
        <Spinner />
      </Center>
    );
  }

  // MI1データが一度も無い場合は月送りも年月も出さず、説明文言だけを表示する
  if (isEmpty) {
    return (
      <Center w="full" py={10} px={4}>
        <Text
          fontSize="sm"
          color="common_text.gray"
          whiteSpace="pre-wrap"
          textAlign="center"
        >
          {t("Graph.monthlyReport.noData")}
        </Text>
      </Center>
    );
  }

  // 取得失敗はトーストのみで通知するため、本文位置は空のままにする
  if (isAvailableError || yearMonth === null) {
    return <Box w="full" />;
  }

  return (
    <VStack w="full" flex={1} gap={4}>
      {/* 対象月の切り替え【左右ナビゲーション付き】 */}
      <MonthlyReportNav
        yearMonth={yearMonth}
        canDecrement={canDecrement}
        canIncrement={canIncrement}
        onDecrement={decrement}
        onIncrement={increment}
      />

      {/* レポート本文 */}
      {isReportLoading ? (
        <Center w="full" py={10}>
          <Spinner />
        </Center>
      ) : (
        content !== null && (
          <MonthlyReportSections yearMonth={yearMonth} report={content} />
        )
      )}
    </VStack>
  );
}

/*
 * レイアウトパーツ：対象月の表示と左右ナビゲーション
 */
function MonthlyReportNav(
  props: Readonly<{
    yearMonth: string;
    canDecrement: boolean;
    canIncrement: boolean;
    onDecrement: () => void;
    onIncrement: () => void;
  }>,
): ReactElement {
  return (
    <Box
      w="100%"
      sx={{
        ".analysis_arrow_left": {
          fontSize: "1.5rem",
          backgroundColor: "primary.bg_lv1",
          paddingLeft: "10px",
        },
        ".analysis_arrow_right": {
          fontSize: "1.5rem",
          backgroundColor: "primary.bg_lv1",
          paddingRight: "10px",
        },
      }}
    >
      <Grid
        w="100%"
        templateColumns="auto 1fr auto"
        gap={2}
        alignItems="center"
      >
        <IconButton
          icon={<ChevronLeftIcon />}
          isDisabled={!props.canDecrement}
          onClick={props.onDecrement}
          className="analysis_arrow_left"
          aria-label="Decrement"
          data-testid="monthly-report-decrement"
        />
        <Flex justifyContent="center" alignItems="center">
          <NumericDataText
            display="inline-flex"
            fontSize="xl"
            letterSpacing={0.5}
            as="div"
            data-testid="monthly-report-year-month"
          >
            {dayjs(props.yearMonth).format("YYYY/M")}
          </NumericDataText>
        </Flex>
        <IconButton
          icon={<ChevronRightIcon />}
          isDisabled={!props.canIncrement}
          onClick={props.onIncrement}
          className="analysis_arrow_right"
          aria-label="Increment"
          data-testid="monthly-report-increment"
        />
      </Grid>
    </Box>
  );
}

type MonthlyStats = {
  /** 一覧が未取得なら null */
  count: number | null;
  /** 一覧が未取得なら null */
  days: number | null;
  /** 対象データが無ければ null */
  mi1Ave: number | null;
  /** 100倍した値。対象データが無ければ null */
  mentalActivityAve: number | null;
};

/** 平均値の前月比を出すために前月・当月それぞれで必要な測定回数 */
const MIN_COUNT_FOR_AVERAGE_DIFF = 10;

/** 一覧が未取得・取得失敗のときの表示値 */
const UNFETCHED_MONTHLY_STATS: MonthlyStats = {
  count: null,
  days: null,
  mi1Ave: null,
  mentalActivityAve: null,
};

/** 指定月(YYYY-MM)の測定回数・測定日数・平均値を集計する */
function calcMonthlyStats(
  mimosysData: MimosysDateType[],
  month: dayjs.Dayjs,
): MonthlyStats {
  const monthData = mimosysData.filter((value) =>
    dayjs(value.recorded_at).isSame(month, "month"),
  );
  const days = new Set(
    monthData.map((value) => dayjs(value.recorded_at).format("YYYY-MM-DD")),
  ).size;
  const mi1List = monthData.flatMap((value) =>
    value.mi1_result !== undefined ? [value.mi1_result] : [],
  );
  return {
    count: monthData.length,
    days,
    mi1Ave:
      mi1List.length > 0
        ? mi1List.reduce((total, next) => total + next, 0) / mi1List.length
        : null,
    mentalActivityAve:
      monthData.length > 0
        ? (monthData.reduce((total, next) => total + next.mental_activity, 0) /
            monthData.length) *
          100
        : null,
  };
}

/*
 * レイアウトパーツ：当月の測定状況・平均値を前月と並べて表示するカード群
 */
function MonthlyReportOverview(
  props: Readonly<{ yearMonth: string }>,
): ReactElement {
  const t = useModifiedTranslation();
  const [mimosysData] = useAtom(MimosysDataAscAtom);
  const [{ data: mimosysDataList }] = useAtom(mimosysDataQueryAtom);
  const [outputStenScore] = useAtom(outputStenScoreAtom);

  // 一覧が未取得のときは 0 件と区別できないため、全項目を未取得(--)表示にする
  const hasMimosysData = mimosysDataList !== undefined;
  const current = dayjs(props.yearMonth);
  const thisMonth = hasMimosysData
    ? calcMonthlyStats(mimosysData, current)
    : UNFETCHED_MONTHLY_STATS;
  const lastMonth = hasMimosysData
    ? calcMonthlyStats(mimosysData, current.subtract(1, "month"))
    : UNFETCHED_MONTHLY_STATS;
  const averageSuffix = t("Graph.monthlyReport.averageSuffix");
  const showAverageDiff =
    (thisMonth.count ?? 0) >= MIN_COUNT_FOR_AVERAGE_DIFF &&
    (lastMonth.count ?? 0) >= MIN_COUNT_FOR_AVERAGE_DIFF;
  // 前月・当月のどちらかが測定回数を満たさないときは前月の平均も表示しない
  const hideLastMonthAverage = !showAverageDiff;

  return (
    <SimpleGrid w="100%" columns={2} spacing={3}>
      <OverviewCard
        label={t("Graph.monthlyReport.measurementCount")}
        thisMonth={thisMonth.count}
        lastMonth={lastMonth.count}
        unit={t("Graph.monthlyReport.countUnit")}
        fractionDigits={0}
        showDiff
      />
      <OverviewCard
        label={t("Graph.monthlyReport.measurementDays")}
        thisMonth={thisMonth.days}
        lastMonth={lastMonth.days}
        unit={t("Graph.monthlyReport.dayUnit")}
        fractionDigits={0}
        showDiff
      />
      {/* こころ指数は契約ありのときのみ表示する */}
      {outputStenScore && (
        <OverviewCard
          label={`${t("Graph.common.mi1Result")}${averageSuffix}`}
          thisMonth={thisMonth.mi1Ave}
          lastMonth={hideLastMonthAverage ? null : lastMonth.mi1Ave}
          unit=""
          fractionDigits={1}
          showDiff={showAverageDiff}
        />
      )}
      <OverviewCard
        label={`${t("Graph.common.mentalActivity")}${averageSuffix}`}
        thisMonth={thisMonth.mentalActivityAve}
        lastMonth={hideLastMonthAverage ? null : lastMonth.mentalActivityAve}
        unit=""
        fractionDigits={1}
        showDiff={showAverageDiff}
      />
    </SimpleGrid>
  );
}

/*
 * レイアウトパーツ：数値カード1件(項目名 / 当月値 / 前月値と差分)
 */
function OverviewCard(
  props: Readonly<{
    label: string;
    thisMonth: number | null;
    lastMonth: number | null;
    unit: string;
    fractionDigits: number;
    /** 前月比を表示するか */
    showDiff: boolean;
  }>,
): ReactElement {
  const t = useModifiedTranslation();
  const format = (value: number | null): string =>
    value === null ? "--" : value.toFixed(props.fractionDigits);

  let diffText = "";
  if (props.showDiff && props.thisMonth !== null && props.lastMonth !== null) {
    const diff = Number(
      (props.thisMonth - props.lastMonth).toFixed(props.fractionDigits),
    );
    let sign = "±";
    if (diff > 0) sign = "+";
    else if (diff < 0) sign = "-";
    diffText = `（${sign}${Math.abs(diff).toFixed(props.fractionDigits)}）`;
  }
  const lastMonthUnit = props.lastMonth === null ? "" : props.unit;

  return (
    <MonthlyReportCard>
      <Text fontSize="xs" color="gray.500">
        {props.label}
      </Text>
      <Flex alignItems="baseline" mt={1}>
        <NumericDataText fontSize="2xl" fontWeight="bold" lineHeight="1.2">
          {format(props.thisMonth)}
        </NumericDataText>
        {props.thisMonth !== null && (
          <Text fontSize="sm" fontWeight="bold" ml={0.5}>
            {props.unit}
          </Text>
        )}
      </Flex>
      <NumericDataText fontSize="xs" color="gray.500" mt={1}>
        {`${t("Graph.monthlyReport.lastMonth")} ${format(props.lastMonth)}${lastMonthUnit}${diffText}`}
      </NumericDataText>
    </MonthlyReportCard>
  );
}

/*
 * レイアウトパーツ：レポート本文の各セクション
 */
function MonthlyReportSections(
  props: Readonly<{ yearMonth: string; report: MonthlyReportContent }>,
): ReactElement {
  const t = useModifiedTranslation();
  const { report } = props;
  const [outputStenScore] = useAtom(outputStenScoreAtom);

  return (
    <VStack w="100%" gap={3}>
      <MonthlyReportOverview yearMonth={props.yearMonth} />
      <MonthlyReportSection
        title={t("Graph.monthlyReport.summary")}
        text={report.monthly_summary.text}
        testId="monthly-report-summary"
      />
      {/* こころ指数の解説は契約ありかつAPIが返したときのみ表示する */}
      {outputStenScore && report.mind_index != null && (
        <MonthlyReportSection
          title={t("Graph.common.mi1Result")}
          text={report.mind_index.text}
          testId="monthly-report-mind-index"
        />
      )}
      <MonthlyReportSection
        title={t("Graph.common.mentalActivity")}
        text={report.mental_activity.text}
        testId="monthly-report-mental-activity"
      />
      <MonthlyReportSection
        title={t("Graph.monthlyReport.emotionBalance")}
        text={report.emotion_balance.text}
        testId="monthly-report-emotion-balance"
      />
      <MonthlyReportSection
        title={t("Graph.monthlyReport.measurementStatus")}
        text={report.measurement_status.text}
        testId="monthly-report-measurement-status"
      />
    </VStack>
  );
}

/*
 * レイアウトパーツ：カード枠
 */
function MonthlyReportCard(
  props: Readonly<{ children: ReactNode }>,
): ReactElement {
  return (
    <Box w="100%" p={4} bg="primary.bg_lv1" borderRadius="xl">
      {props.children}
    </Box>
  );
}

/*
 * レイアウトパーツ：セクション1件
 */
function MonthlyReportSection(
  props: Readonly<{ title: string; text: string; testId: string }>,
): ReactElement {
  return (
    <MonthlyReportCard>
      <Text fontSize="md" fontWeight="bold" color="primary.theme_lv1">
        {props.title}
      </Text>
      {/* API文言に改行が含まれるため pre-wrap で保持する */}
      <Text
        fontSize="sm"
        lineHeight="1.8"
        textAlign="left"
        whiteSpace="pre-wrap"
        mt={2}
        data-testid={props.testId}
      >
        {props.text}
      </Text>
    </MonthlyReportCard>
  );
}
